<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Monthly Breakup</h5>
          <small class="text-muted"
            >Manage monthly topic / subtopic breakup by class &amp; subject</small
          >
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              append-to-body
              label="label"
              :clearable="false"
              :searchable="false"
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadItems"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-5 d-flex align-items-center"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input"
              :placeholder="$t('Search') + '...'"
              @input="onSearchChange"
            />
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              append-to-body
              placeholder="Class"
              class="filter-control filter-field-item"
              @input="onFilterClassChange"
            />
            <v-select
              v-model="filters.sectionID"
              :options="filterSections"
              :clearable="true"
              multiple
              :close-on-select="false"
              label="section"
              :reduce="(val) => val.id"
              append-to-body
              placeholder="Section"
              :disabled="!filters.classID"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="filterSubjects"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              append-to-body
              placeholder="Subject"
              :disabled="isTeacherScoped && !filters.classID"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.month"
              :options="monthOptions"
              :clearable="true"
              label="label"
              :reduce="(val) => val.value"
              append-to-body
              placeholder="Month"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.sessionID"
              :options="sessions"
              :clearable="true"
              label="session"
              :reduce="(val) => val.id"
              append-to-body
              placeholder="Session"
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.view"
              variant="outline-primary"
              class="action-btn-filter add-sheet-btn text-nowrap mr-50"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              @click="openPrintModal"
            >
              <feather-icon icon="PrinterIcon" class="mr-50" />
              <span>Print</span>
            </b-button>
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Breakup</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="items"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <div class="breakup-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="breakup-skeleton__row"
                :style="{ gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))` }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="breakup-skeleton__cell"
                >
                  <div
                    class="breakup-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(className)="data">
            <b-badge variant="light-primary">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(sectionName)="data">
            <b-badge variant="light-info">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(subjectName)="data">
            <b-badge variant="light-success">
              {{ data.value || "-" }}
            </b-badge>
          </template>

          <template #cell(monthYear)="data">
            <b-badge variant="light-secondary">
              {{ formatMonthYear(data.item) }}
            </b-badge>
          </template>

          <template #cell(sessionName)="data">
            <b-badge variant="light-info">
              {{ data.value || getSessionName(data.item.sessionID) || "-" }}
            </b-badge>
          </template>

          <template #cell(topic)="data">
            <div class="text-clamp" :title="data.value || ''">
              {{ data.value || "-" }}
            </div>
          </template>

          <template #cell(subTopic)="data">
            <div class="text-clamp" :title="data.value || ''">
              {{ data.value || "-" }}
            </div>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex align-items-center justify-content-center">
              <b-button
                v-if="rights.view"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="View"
                size="sm"
                class="action-btn-table mr-1"
                @click="viewItem(item)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>

              <b-button
                v-if="rights.edit"
                variant="outline-primary"
                v-b-tooltip.hover.top
                title="Edit"
                size="sm"
                class="action-btn-outline edit-btn mr-1"
                @click="editItem(item)"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                size="sm"
                class="action-btn-outline delete-btn"
                @click="deleteItem(item)"
              >
                <feather-icon icon="Trash2Icon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-modal
      v-model="showModal"
      :title="modalTitle"
      size="lg"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
      modal-class="breakup-form-modal"
      backdrop-class="breakup-form-backdrop"
    >
      <div v-if="isView" class="breakup-view">
        <div class="breakup-view__meta">
          <div class="breakup-view__row">
            <span class="breakup-view__label">Class</span>
            <span class="breakup-view__value">{{ viewLabels.className }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Section</span>
            <span class="breakup-view__value">{{ viewLabels.sectionName }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Subject</span>
            <span class="breakup-view__value">{{ viewLabels.subjectName }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Month</span>
            <span class="breakup-view__value">{{ viewLabels.monthName }}</span>
          </div>
          <div class="breakup-view__row">
            <span class="breakup-view__label">Session</span>
            <span class="breakup-view__value">{{ viewLabels.sessionName }}</span>
          </div>
        </div>
        <div class="breakup-view__section">
          <div class="breakup-view__section-label">Topic</div>
          <div class="breakup-view__section-text breakup-view__section-text--sm">
            {{ form.topic || "-" }}
          </div>
        </div>
        <div class="breakup-view__section">
          <div class="breakup-view__section-label">Sub Topic</div>
          <div class="breakup-view__section-text">
            {{ form.subTopic || "-" }}
          </div>
        </div>
      </div>

      <b-form v-else @submit.prevent>
        <b-row>
          <b-col md="4">
            <b-form-group label="Class *" label-for="mb-class">
              <v-select
                id="mb-class"
                v-model="form.classID"
                :options="classes"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
                append-to-body
                placeholder="Select class"
                @input="onFormClassChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Section *" label-for="mb-section">
              <v-select
                id="mb-section"
                v-model="form.sectionID"
                :options="formSections"
                :clearable="true"
                multiple
                :close-on-select="false"
                label="section"
                :reduce="(val) => val.id"
                append-to-body
                placeholder="Select section"
                :disabled="!form.classID"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Subject *" label-for="mb-subject">
              <v-select
                id="mb-subject"
                v-model="form.subjectID"
                :options="formSubjects"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
                append-to-body
                placeholder="Select subject"
                :disabled="isTeacherScoped && !form.classID"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Month *" label-for="mb-month">
              <v-select
                id="mb-month"
                v-model="form.month"
                :options="monthOptions"
                :clearable="false"
                label="label"
                :reduce="(val) => val.value"
                append-to-body
                placeholder="Month"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Session *" label-for="mb-session">
              <v-select
                id="mb-session"
                v-model="form.sessionID"
                :options="sessions"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
                append-to-body
                placeholder="Select session"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Topic *" label-for="mb-topic">
              <b-form-input
                id="mb-topic"
                v-model.trim="form.topic"
                placeholder="Enter topic"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group label="Sub Topic" label-for="mb-subtopic">
              <b-form-textarea
                id="mb-subtopic"
                v-model.trim="form.subTopic"
                placeholder="Enter sub topic"
                rows="4"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <template v-if="isView">
          <b-button variant="outline-secondary" @click="showModal = false">
            Close
          </b-button>
          <b-button
            v-if="rights.edit"
            variant="outline-primary"
            class="breakup-edit-outline-btn"
            @click="switchToEdit"
          >
            <feather-icon icon="EditIcon" class="mr-50" />
            Edit
          </b-button>
        </template>
        <template v-else>
          <b-button variant="outline-secondary" @click="showModal = false">
            Cancel
          </b-button>
          <b-button variant="primary" :disabled="loading" @click="saveItem">
            <b-spinner v-if="loading" small />
            {{ isEdit ? "Update" : "Save" }}
          </b-button>
        </template>
      </template>
    </b-modal>

    <b-modal
      v-model="showPrintModal"
      title="Print Monthly Breakup"
      centered
      no-close-on-backdrop
      modal-class="breakup-form-modal"
      backdrop-class="breakup-form-backdrop"
      @hidden="resetPrintForm"
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Class *" label-for="mb-print-class">
              <v-select
                id="mb-print-class"
                v-model="printForm.classIDs"
                multiple
                :close-on-select="false"
                :options="printClassOptions"
                :clearable="false"
                label="name"
                :reduce="(val) => val.id"
              append-to-body
                placeholder="Select class(es)"
                @input="onPrintClassChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Section" label-for="mb-print-section">
              <v-select
                id="mb-print-section"
                v-model="printForm.sectionID"
                :options="printSections"
                :clearable="false"
                label="section"
                :reduce="(val) => val.id"
              append-to-body
                placeholder="All sections"
                :disabled="!canSelectPrintSection"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Subject *" label-for="mb-print-subject">
              <v-select
                id="mb-print-subject"
                v-model="printForm.subjectIDs"
                multiple
                :close-on-select="false"
                :options="printSubjectOptions"
                :clearable="false"
                label="subject"
                :reduce="(val) => val.id"
              append-to-body
                placeholder="Select subject(s)"
                @input="onPrintSubjectChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Session" label-for="mb-print-session">
              <v-select
                id="mb-print-session"
                v-model="printForm.sessionID"
                :options="[{ id: 0, session: 'All Sessions' }, ...sessions]"
                :clearable="false"
                label="session"
                :reduce="(val) => val.id"
              append-to-body
                placeholder="Select session"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showPrintModal = false">
          Cancel
        </b-button>
        <b-button variant="primary" @click="goToPrint">
          <feather-icon icon="PrinterIcon" class="mr-50" />
          Print Preview
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BFormTextarea,
  BButton,
  BTable,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
  BBadge,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import teacherPlannerScopeMixin from "@/views/myskool/planner/teacherPlannerScopeMixin";

const MONTH_OPTIONS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

export default {
  name: "MonthlyBreakup",
  mixins: [teacherPlannerScopeMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BFormTextarea,
    BButton,
    BTable,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    BBadge,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    const now = new Date();
    return {
      rights: {},
      items: [],
      classes: [],
      classesRaw: [],
      sessions: [],
      filterSections: [],
      filterSubjects: [],
      formSections: [],
      formSubjects: [],
      printSections: [],
      printSubjects: [],
      searchQuery: "",
      searchTimeout: null,
      showModal: false,
      showPrintModal: false,
      isEdit: false,
      isView: false,
      loading: false,
      dataLoading: true,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      monthOptions: MONTH_OPTIONS,
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "className", label: "Class" },
        { key: "sectionName", label: "Section" },
        { key: "subjectName", label: "Subject" },
        { key: "monthYear", label: "Month" },
        { key: "sessionName", label: "Session" },
        { key: "topic", label: "Topic", tdClass: "cell-wrap", thClass: "cell-wrap" },
        { key: "subTopic", label: "Sub Topic", tdClass: "cell-wrap", thClass: "cell-wrap" },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: {
        id: 0,
        classID: null,
        sectionID: [],
        subjectID: null,
        month: now.getMonth() + 1,
        sessionID: null,
        topic: "",
        subTopic: "",
        tID: null,
      },
      filters: {
        classID: null,
        sectionID: [],
        subjectID: null,
        month: null,
        sessionID: null,
        tID: null,
      },
      printForm: {
        classIDs: [0],
        sectionID: 0,
        subjectIDs: [0],
        sessionID: 0,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    printClassOptions() {
      return [{ id: 0, name: "All Classes" }, ...(this.classes || [])];
    },
    printSubjectOptions() {
      return [
        { id: 0, subject: "All Subjects" },
        ...(this.printSubjects || []),
      ];
    },
    canSelectPrintSection() {
      const ids = this.printForm.classIDs || [];
      return ids.length === 1 && Number(ids[0]) !== 0;
    },
    modalTitle() {
      if (this.isView) return "View Monthly Breakup";
      if (this.isEdit) return "Edit Monthly Breakup";
      return "Add Monthly Breakup";
    },
    viewLabels() {
      const className =
        this.classes.find((c) => Number(c.id) === Number(this.form.classID))
          ?.name || "-";
      const sectionName =
        this.formSections.find(
          (s) => Number(s.id) === Number(this.form.sectionID)
        )?.section || "-";
      const subjectName =
        this.formSubjects.find(
          (s) => Number(s.id) === Number(this.form.subjectID)
        )?.subject || "-";
      const monthName =
        this.monthOptions.find(
          (m) => Number(m.value) === Number(this.form.month)
        )?.label || "-";
      const sessionName = this.getSessionName(this.form.sessionID) || "-";
      return { className, sectionName, subjectName, monthName, sessionName };
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  async created() {
    if (!this.initPageRights()) return;

    await this.initTeacherPlannerScope();
    await Promise.all([this.loadClasses(), this.loadSessions()]);
    if (!this.isTeacherScoped) {
      await this.loadAllPlannerSubjects();
      this.filterSubjects = this.allSubjects;
      this.formSubjects = this.allSubjects;
      this.printSubjects = this.allSubjects;
    }
    await this.loadItems();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    initPageRights() {
      const right = {};
      (this.$store.state.menu || []).forEach((el) => {
        (el.children || []).forEach((ch) => {
          right[ch.route] = {
            view: ch.view,
            add: ch.add,
            edit: ch.edit,
            delete: ch.delete,
            assignSections: ch.assignSections,
          };
        });
      });
      this.$store.commit("setRights", right);

      if (!this.$store.state.rights[this.$route.name]) {
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }
      this.rights = this.$store.state.rights[this.$route.name] || {};
      if (!this.rights.view) {
        this.$router.replace({ name: "misc-not-authorized" });
        return false;
      }
      return true;
    },

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },

    getSessionName(sessionID) {
      if (!sessionID) return "";
      const found = this.sessions.find(
        (s) => Number(s.id) === Number(sessionID)
      );
      return found ? found.session : "";
    },

    formatMonthYear(item) {
      const monthLabel =
        MONTH_OPTIONS.find((m) => Number(m.value) === Number(item.month))
          ?.label || item.month;
      return monthLabel || "-";
    },

    async loadSessions() {
      try {
        const dbName = encodeURIComponent(this.$store.state.userData.db);
        const cID = this.$store.state.userData.cId;
        const url = `${this.$store.state.domain}Sessions?db=${dbName}&cID=${cID}`;
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        const data =
          (response && response.data && Array.isArray(response.data)
            ? response.data
            : null) || (Array.isArray(response) ? response : []);
        this.sessions = data;
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    getClassSections(classID) {
      if (!classID) return [];

      if (this.isTeacherScoped) {
        const classItem = (this.teacherClassesData || []).find((item) => {
          const id = item.cID ?? item.clsID ?? item.classID ?? null;
          return Number(id) === Number(classID);
        });
        const sections = classItem?.secSubjectsData || [];
        return sections.map((sec) => ({
          id: sec.sectionID ?? sec.id,
          section:
            sec.sectionName ||
            sec.section ||
            sec.name ||
            `Section ${sec.sectionID ?? sec.id}`,
        }));
      }

      const raw = this.classesRaw.find(
        (el) => Number(el?.cls?.id ?? el?.id) === Number(classID)
      );
      const sections =
        raw?.cls?.sections ||
        raw?.sections ||
        this.classes.find((c) => Number(c.id) === Number(classID))?.sections ||
        [];
      return Array.isArray(sections)
        ? sections.map((sec) => ({
            id: sec.id,
            section: sec.section || sec.name || `Section ${sec.id}`,
          }))
        : [];
    },

    async loadClasses() {
      if (this.isTeacherScoped) {
        this.classes = await this.loadTeacherClasses();
        this.classesRaw = this.teacherClassesData || [];
        return;
      }

      const obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.plannerCampusID +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      const classesData = await this.get(obj);
      const list = classesData?.data || classesData || [];
      this.classesRaw = Array.isArray(list) ? list : [];
      this.classes = this.classesRaw.map((el) => el.cls).filter(Boolean);
    },

    buildListRequest(page, pageSize) {
      let url =
        this.$store.state.domain +
        "MonthlyBreakup/GetByPagination?db=" +
        this.$store.state.userData.db +
        "&campusID=" +
        this.plannerCampusID +
        "&page=" +
        page +
        "&pageSize=" +
        pageSize;

      const scopeParts = [];
      this.appendTeacherScopeQueryParts(
        scopeParts,
        this.filters.classID || null
      );
      url += "&" + scopeParts.join("&");

      url +=
        "&sectionID=" +
        (Array.isArray(this.filters.sectionID) ? this.filters.sectionID.join(",") : this.filters.sectionID || "") +
        "&subjectID=" +
        (this.filters.subjectID || "") +
        "&month=" +
        (this.filters.month || "") +
        "&sessionID=" +
        (this.filters.sessionID || "");

      if (!this.isTeacherScoped) {
        url += "&tID=";
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        url += "&search=" + encodeURIComponent(this.searchQuery.trim());
      }

      return {
        url,
        token: this.$store.state.userData.token,
        dashboard: true,
      };
    },

    async loadItems(page) {
      if (page) {
        this.currentPage = page;
      }
      this.dataLoading = true;
      try {
        if (this.isTeacherScoped) {
          const cacheKey = [
            this.filters.classID,
            this.filters.sectionID,
            this.filters.subjectID,
            this.filters.month,
            this.filters.sessionID,
            this.searchQuery,
            this.assignedClassIDs.join(","),
          ].join("|");
          const allItems = await this.fetchTeacherScopedPaginatedData({
            cacheKey,
            fetchPage: (p, ps) => this.get(this.buildListRequest(p, ps)),
          });
          const { items, totalCount } = this.getTeacherScopedPageSlice(allItems);
          this.items = items;
          this.totalCount = totalCount;
        } else {
          const res = await this.get(
            this.buildListRequest(this.currentPage, this.perPage)
          );
          if (res && Array.isArray(res.data)) {
            this.items = res.data;
            this.totalCount = res.totalCount || res.total || res.data.length;
          } else if (Array.isArray(res)) {
            this.items = res;
            this.totalCount = res.length;
          } else {
            this.items = [];
            this.totalCount = 0;
          }
        }
      } finally {
        this.dataLoading = false;
      }
    },

    async onFilterClassChange(classID) {
      this.invalidateTeacherScopedCache();
      this.filters.sectionID = [];
      this.filters.subjectID = null;
      this.filterSections = this.getClassSections(classID);
      this.filterSubjects = this.updateScopedSubjects(classID);
      this.currentPage = 1;
      this.loadItems();
    },

    onFilterChange() {
      this.invalidateTeacherScopedCache();
      this.currentPage = 1;
      this.loadItems();
    },

    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.invalidateTeacherScopedCache();
        this.currentPage = 1;
        this.loadItems();
      }, 500);
    },

    onFormClassChange(classID) {
      this.form.sectionID = [];
      this.form.subjectID = null;
      this.formSections = this.getClassSections(classID);
      this.formSubjects = this.updateScopedSubjects(classID);
    },

    openAddModal() {
      this.isEdit = false;
      this.isView = false;
      this.resetForm();
      this.showModal = true;
    },

    openPrintModal() {
      const classIDs =
        this.filters.classID != null && this.filters.classID !== ""
          ? [this.filters.classID]
          : [0];
      const subjectIDs =
        this.filters.subjectID != null && this.filters.subjectID !== ""
          ? [this.filters.subjectID]
          : [0];

      this.printForm = {
        classIDs,
        sectionID: this.filters.sectionID || 0,
        subjectIDs,
        sessionID: this.filters.sessionID || 0,
      };
      this.refreshPrintSections();
      this.refreshPrintSubjects();
      this.showPrintModal = true;
    },

    onPrintClassChange(classIDs) {
      const ids = Array.isArray(classIDs) ? [...classIDs] : [];
      if (ids.length && Number(ids[ids.length - 1]) === 0) {
        this.printForm.classIDs = [0];
      } else {
        this.printForm.classIDs = ids.filter((id) => Number(id) !== 0);
      }
      this.printForm.sectionID = 0;
      this.refreshPrintSections();
      this.refreshPrintSubjects();
    },

    onPrintSubjectChange(subjectIDs) {
      const ids = Array.isArray(subjectIDs) ? [...subjectIDs] : [];
      if (ids.length && Number(ids[ids.length - 1]) === 0) {
        this.printForm.subjectIDs = [0];
      } else {
        this.printForm.subjectIDs = ids.filter((id) => Number(id) !== 0);
      }
    },

    refreshPrintSections() {
      const ids = this.printForm.classIDs || [];
      if (ids.length === 1 && Number(ids[0]) !== 0) {
        this.printSections = [
          { id: 0, section: "All sections" },
          ...this.getClassSections(ids[0]),
        ];
      } else {
        this.printSections = [{ id: 0, section: "All sections" }];
      }
    },

    refreshPrintSubjects() {
      const ids = this.printForm.classIDs || [];
      if (ids.length === 1 && Number(ids[0]) !== 0) {
        this.printSubjects = this.updateScopedSubjects(ids[0]);
      } else if (!this.isTeacherScoped) {
        this.printSubjects = this.allSubjects || [];
      } else {
        // teacher + all/multi classes: merge unique subjects from selected classes
        const map = new Map();
        const classList =
          ids.includes(0) || !ids.length
            ? (this.classes || []).map((c) => c.id)
            : ids;
        classList.forEach((classID) => {
          (this.updateScopedSubjects(classID) || []).forEach((sub) => {
            if (sub?.id != null && !map.has(Number(sub.id))) {
              map.set(Number(sub.id), sub);
            }
          });
        });
        this.printSubjects = Array.from(map.values());
      }
      // keep subject selection valid
      const validIds = new Set(
        (this.printSubjects || []).map((s) => Number(s.id))
      );
      const selected = this.printForm.subjectIDs || [];
      if (selected.includes(0)) {
        this.printForm.subjectIDs = [0];
      } else {
        const next = selected.filter((id) => validIds.has(Number(id)));
        this.printForm.subjectIDs = next.length ? next : [0];
      }
    },

    resetPrintForm() {
      this.printForm = {
        classIDs: [0],
        sectionID: 0,
        subjectIDs: [0],
        sessionID: 0,
      };
      this.printSections = [{ id: 0, section: "All sections" }];
      this.printSubjects = this.isTeacherScoped ? [] : this.allSubjects || [];
    },

    goToPrint() {
      const classIDs = Array.isArray(this.printForm.classIDs)
        ? this.printForm.classIDs
        : [];
      const subjectIDs = Array.isArray(this.printForm.subjectIDs)
        ? this.printForm.subjectIDs
        : [];

      if (!classIDs.length) {
        this.$bvToast.toast("Please select at least one class!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }
      if (!subjectIDs.length) {
        this.$bvToast.toast("Please select at least one subject!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      const className = classIDs.includes(0)
        ? "All Classes"
        : classIDs
            .map(
              (id) =>
                this.classes.find((c) => Number(c.id) === Number(id))?.name ||
                ""
            )
            .filter(Boolean)
            .join(", ");
      const subjectName = subjectIDs.includes(0)
        ? "All Subjects"
        : subjectIDs
            .map(
              (id) =>
                this.printSubjectOptions.find((s) => Number(s.id) === Number(id))
                  ?.subject || ""
            )
            .filter(Boolean)
            .join(", ");
      const sectionName =
        this.printSections.find(
          (s) => Number(s.id) === Number(this.printForm.sectionID)
        )?.section || "All sections";
      const campus =
        this.$store.state.currentBranch ||
        this.$store.state.branches?.find(
          (b) => Number(b.id) === Number(this.plannerCampusID)
        ) ||
        {};

      const route = this.$router.resolve({
        name: "monthly-breakup-print",
        query: {
          db: this.$store.state.userData.db,
          campusID: this.plannerCampusID,
          classIDs: classIDs.join(","),
          sectionID: this.canSelectPrintSection
            ? this.printForm.sectionID || 0
            : 0,
          subjectIDs: subjectIDs.join(","),
          sessionID: this.printForm.sessionID || 0,
          sessionName: this.getSessionName(this.printForm.sessionID) || "",
          className,
          sectionName,
          subjectName,
          campusName:
            campus.name || campus.branch || this.$store.state.branchName || "",
          campusTagline: campus.tagline || campus.slogan || "",
          logo: this.$store.state.logo || campus.logo || "",
          tID: this.isTeacherScoped ? this.teacherScopeTID || "" : "",
          teacherName: this.isTeacherScoped
            ? this.$store.state.userData.name ||
              this.$store.state.userData.fullName ||
              ""
            : "",
        },
      });
      window.open(route.href, "_blank");
      this.showPrintModal = false;
    },

    async viewItem(item) {
      this.isView = true;
      this.isEdit = false;
      await this.loadItemIntoForm(item);
    },

    async editItem(item) {
      this.isView = false;
      this.isEdit = true;
      await this.loadItemIntoForm(item);
    },

    switchToEdit() {
      this.isView = false;
      this.isEdit = true;
    },

    async loadItemIntoForm(item) {
      this.loading = true;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "MonthlyBreakup/LoadSelected?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.plannerCampusID +
            "&id=" +
            item.id,
          token: this.$store.state.userData.token,
          dashboard: true,
        });
        const data = res?.data || res || item;
        this.form = {
          id: data.id || item.id,
          classID: data.classID ?? null,
          sectionID: data.sectionID ? String(data.sectionID).split(',').map(Number).filter(Boolean) : [],
          subjectID: data.subjectID ?? null,
          month: data.month ?? null,
          sessionID: data.sessionID ?? null,
          topic: data.topic || "",
          subTopic: data.subTopic || "",
          tID: this.isTeacherScoped
            ? this.teacherScopeTID
            : data.tID || 0,
        };
        this.formSections = this.getClassSections(this.form.classID);
        this.formSubjects = this.updateScopedSubjects(this.form.classID);
        this.showModal = true;
      } catch (error) {
        console.error("Error loading monthly breakup:", error);
        this.$bvToast.toast("Failed to load record", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },

    async deleteItem(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (!result.isConfirmed) return;

      const obj = {
        url:
          this.$store.state.domain +
          "MonthlyBreakup/Delete?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&id=" +
          item.id +
          "&userID=" +
          this.getCurrentUserLinkID(),
        message: "Monthly breakup deleted successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };
      const success = await this.delete(obj);
      if (success) {
        this.invalidateTeacherScopedCache();
        this.loadItems();
      }
    },

    async saveItem() {
      if (
        !this.form.classID ||
        !this.form.sectionID ||
        (Array.isArray(this.form.sectionID) && !this.form.sectionID.length) ||
        !this.form.subjectID ||
        !this.form.month ||
        !this.form.sessionID ||
        !this.form.topic
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;
      const body = {
        id: this.form.id || 0,
        classID: this.form.classID,
        sectionID: Array.isArray(this.form.sectionID) ? this.form.sectionID.join(",") : this.form.sectionID,
        subjectID: this.form.subjectID,
        month: this.form.month,
        sessionID: this.form.sessionID,
        topic: this.form.topic,
        subTopic: this.form.subTopic || "",
        tID: this.isTeacherScoped ? this.teacherScopeTID || 0 : 0,
      };

      const obj = {
        url:
          this.$store.state.domain +
          "MonthlyBreakup/Save?db=" +
          this.$store.state.userData.db +
          "&campusID=" +
          this.plannerCampusID +
          "&userID=" +
          this.getCurrentUserLinkID(),
        body,
        message:
          "Monthly breakup " +
          (this.isEdit ? "updated" : "created") +
          " successfully.",
        context: this,
        token: this.$store.state.userData.token,
      };

      const success = await this.post(obj);
      if (success) {
        this.showModal = false;
        this.invalidateTeacherScopedCache();
        this.loadItems();
      }
      this.loading = false;
    },

    resetForm() {
      const now = new Date();
      this.isEdit = false;
      this.isView = false;
      this.form = {
        id: 0,
        classID: null,
        sectionID: [],
        subjectID: null,
        month: now.getMonth() + 1,
        sessionID: null,
        topic: "",
        subTopic: "",
        tID: this.isTeacherScoped ? this.teacherScopeTID || 0 : 0,
      };
      this.formSections = [];
      this.formSubjects = this.isTeacherScoped ? [] : this.allSubjects;
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadItems();
    },
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/myskool/planner/plannerListTable.scss";

.planner-list-table {
  width: 100% !important;
  max-width: 100%;

  ::v-deep th.cell-wrap,
  ::v-deep td.cell-wrap {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    vertical-align: top;
    max-width: 260px;
    width: 260px;
  }
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
  max-width: 100%;
}

.breakup-view {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.breakup-view__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #e5e7eb;
}

.breakup-view__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  min-width: 0;
  background: #fff;
}

.breakup-view__label {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.breakup-view__value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  text-align: right;
  word-break: break-word;
}

.breakup-view__section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.breakup-view__section-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.breakup-view__section-text {
  padding: 0.95rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fafafa;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 100px;
  max-height: 260px;
  overflow-y: auto;
}

.breakup-view__section-text--sm {
  min-height: auto;
  max-height: 120px;
}

.breakup-edit-outline-btn {
  background: #fff !important;
  border: 1.5px solid #7c5dfa !important;
  color: #7c5dfa !important;
  font-weight: 600;

  &:hover {
    background: #f5f3ff !important;
    border-color: #6d4cdb !important;
    color: #6d4cdb !important;
  }
}

@media (max-width: 768px) {
  .breakup-view__meta {
    grid-template-columns: 1fr;
  }
}

.breakup-skeleton {
  width: 100%;
  padding: 0.35rem 0.25rem 0.75rem;
}

.breakup-skeleton__row {
  display: grid;
  gap: 0.75rem;
  align-items: center;
  padding: 0.85rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.breakup-skeleton__cell {
  min-width: 0;
}

.breakup-skeleton__bar {
  height: 14px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #ecebf8 0%,
    #f7f6ff 40%,
    #ecebf8 80%
  );
  background-size: 200% 100%;
  animation: breakupSkeletonShine 1.2s ease-in-out infinite;
}

@keyframes breakupSkeletonShine {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

::v-deep .filter-fields-group .filter-search-input {
  flex: 1 1 120px !important;
  min-width: 120px !important;
  max-width: 180px !important;
}

::v-deep .filter-control.vs--multiple .vs__selected {
  white-space: nowrap;
}

::v-deep .filter-control.vs--multiple .vs__selected-options {
  flex-wrap: nowrap;
  overflow: hidden;
  align-items: center;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

/*
 * Scoped to Monthly Breakup modals only — do NOT use body.modal-open globally
 * (that breaks Planner Concepts / Topics modals).
 * z-index above page-size dropdown (99999 in plannerListTable.scss).
 */
.modal.breakup-form-modal {
  z-index: 110000 !important;
}

.modal-backdrop.breakup-form-backdrop {
  z-index: 109990 !important;
}

body:has(.modal.breakup-form-modal.show) .vs__dropdown-menu {
  z-index: 110010 !important;
}
</style>
